import React, { useEffect, useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { DashboardLayout } from '../../../../components/layout';
import { AreaToolbar, AreaToolbarButton } from '../../../../components/layout/area-toolbar';
import { StartPageTable } from '../../../../components/start-page-table/start-page-table';
import { useMetaTitle } from '../../../../hooks';
import { EducatorDtoEx } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { attendanceAreaPathName, getEducatorEditPath, getEducatorRedirectPath } from '../../attendance-area-pathnames';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { REFRESH_EDUCATOR_TABLE } from '../educator-uniform/educator';
import { educatorColumns } from './educator-columns';
import { useAttendanceRights } from '../../use-attendance-rights';
import { Restricted } from '../../../../authentication';
import { ResourceTypes, ResourceOperations } from '../../../../authentication/resources';

const client = moduleClientFactory.createAttendanceClient();

export function EducatorsPage() {
  const [educators, setEducators] = useState<EducatorDtoEx[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const { canEditEducators } = useAttendanceRights();

  useMetaTitle('Arbejdsområde Job & tilbud - Underviser');

  async function loadEducators() {
    try {
      setIsLoading(true);
      const result = await client.getEducators();
      setEducators(result.educators || []);
    } catch (error) {
      enqueueSnackbar('Der skete en fejl ved hentning af undervisere', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadEducators();
  }, []);

  usePostToTab(REFRESH_EDUCATOR_TABLE, () => loadEducators());

  function handleCreateClick() {
    window.open(`/${attendanceAreaPathName}/${getEducatorRedirectPath()}`);
  }

  function handleRowClick(row: EducatorDtoEx) {
    window.open(`/${attendanceAreaPathName}/${getEducatorEditPath(row.educatorId)}`);
  }

  return (
    <DashboardLayout
      toolbar={
        <AreaToolbar
          mainItemGuid="43fe2e44-f0a3-4462-9940-467c8dfb1f47"
          prefixedText="Arbejdsområde Job & tilbud - "
          tools={
            canEditEducators
              ? [
                  <AreaToolbarButton key="add" icon={faPlus} color="primary" variant="text" iconPos="left" onClick={handleCreateClick}>
                    Opret ny underviser
                  </AreaToolbarButton>,
                ]
              : []
          }
        />
      }
    >
      <Restricted
        actions={[
          { resourceType: ResourceTypes.AttendanceFacility, resourceOperation: ResourceOperations.Read },
          { resourceType: ResourceTypes.AttendanceFacility, resourceOperation: ResourceOperations.Write },
        ]}
      >
        <StartPageTable<EducatorDtoEx>
          title="Undervisere"
          context="Educators"
          universe="company"
          columns={educatorColumns}
          rows={educators}
          getRowId={row => row.educatorId}
          getRootEntityId={row => row.companyId}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          onRefresh={loadEducators}
        />
      </Restricted>
    </DashboardLayout>
  );
}
